import { styled } from "goober";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import {
  useBranchRunStatuses,
  useRunExecutions,
} from "../hooks/useStepExecutions";
import type { ParsedStep } from "../lib/parseCommitData";
import { routes, useCurrentUrl } from "../lib/urls";
import { RunWaterfall } from "./RunWaterfall";
import { Text } from "./Text";

type Props = {
  workflowId: string;
  branchId: string;
  steps: ReadonlyArray<ParsedStep>;
  hoveredCanonicalId: string | null;
  onStepHover: (canonicalId: string | null) => void;
  onActiveRunStepsChange?: (canonicalIds: ReadonlyArray<string> | null) => void;
};

const AXIS_HEIGHT = 34;
const FADE_X = 24;
const FADE_Y = 16;
const TICK_INSET = 24;
const FAKE_TOTAL_MS = 30_000;

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
}

const Outer = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
`;

const GhostWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

const GhostHeader = styled("div")`
  margin-top: 32px;
  display: flex;
  padding: 0 32px 16px;
  gap: 32px;
  opacity: 0.4;
`;

const GhostStat = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 76px;
`;

const IdleBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px 1px 5px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 500;
  line-height: 14px;
  width: fit-content;
  color: var(--text-secondary);
  background: var(--alpha8);
`;

const IdleBadgeDot = styled("span")`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--alpha8);
`;

const GhostChart = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  mask-image: linear-gradient(to bottom, transparent, black ${FADE_Y}px);
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GhostBarsStack = styled("div")`
  margin-top: auto;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0 0;
`;

const GhostBarRow = styled("div")`
  position: relative;
  flex-shrink: 0;
  height: 16px;
  margin: 0 32px;
`;

const GhostBarQueue = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--alpha4);
  border-radius: 8px 0 0 8px;
`;

const GhostBarRun = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--alpha8);
  border-radius: 0 8px 8px 0;

  &[data-no-queue="true"] {
    border-radius: 8px;
  }
`;

const GhostAxisContainer = styled("div")`
  margin-top: 8px;
  padding: 0 32px 32px;
`;

const GHOST_BARS: ReadonlyArray<{
  left: number;
  width: number;
  queue: number;
}> = [
  { left: 0, width: 15, queue: 0 },
  { left: 8, width: 30, queue: 4 },
  { left: 30, width: 12, queue: 0 },
  { left: 35, width: 32, queue: 5 },
  { left: 55, width: 8, queue: 0 },
  { left: 62, width: 28, queue: 6 },
  { left: 88, width: 10, queue: 0 },
];

function GhostAxis() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const uid = useId();
  const fadeGradId = `${uid}-axis-fade`;
  const fadeMaskId = `${uid}-axis-mask`;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ticks = useMemo(() => {
    const tickCount = 5;
    const result: { offset: number; label: string }[] = [];
    for (let i = 0; i <= tickCount; i++) {
      const frac = i / tickCount;
      result.push({
        offset: frac,
        label: formatDuration(frac * FAKE_TOTAL_MS),
      });
    }
    return result;
  }, []);

  const tickAreaWidth = Math.max(width - TICK_INSET * 2, 0);
  const fadeStartPct = width > 0 ? (FADE_X / width) * 100 : 0;
  const fadeEndPct = width > 0 ? ((width - FADE_X) / width) * 100 : 100;

  return (
    <GhostAxisContainer ref={ref}>
      <svg
        width={width}
        height={AXIS_HEIGHT}
        style={{ flexShrink: 0, opacity: 0.4 }}
      >
        <defs>
          <linearGradient id={fadeGradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop
              offset={`${fadeStartPct}%`}
              stopColor="white"
              stopOpacity="1"
            />
            <stop offset={`${fadeEndPct}%`} stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <mask id={fadeMaskId}>
            <rect
              x="0"
              y="0"
              width={width}
              height={AXIS_HEIGHT}
              fill={`url(#${fadeGradId})`}
            />
          </mask>
        </defs>
        <g mask={`url(#${fadeMaskId})`}>
          <line
            x1={0}
            y1={1}
            x2={width}
            y2={1}
            stroke="var(--gray-400)"
            strokeWidth="2"
          />
          {ticks.map((tick) => (
            <text
              key={tick.offset}
              x={TICK_INSET + tick.offset * tickAreaWidth}
              y={AXIS_HEIGHT - 8}
              textAnchor="middle"
              fill="var(--text-secondary)"
              fontSize="10"
              fontFamily="var(--font-mono)"
              fontWeight="350"
            >
              {tick.label}
            </text>
          ))}
        </g>
      </svg>
    </GhostAxisContainer>
  );
}

function GhostedWaterfall() {
  return (
    <GhostWrapper>
      <GhostHeader>
        <GhostStat>
          <Text variant="text-small" color="var(--text-secondary)">
            Runs
          </Text>
          <IdleBadge>
            <IdleBadgeDot />
            Idle
          </IdleBadge>
        </GhostStat>
        <GhostStat>
          <Text variant="text-small" color="var(--text-secondary)">
            Run
          </Text>
          <Text variant="text-regular-md" color="var(--text-secondary)">
            –
          </Text>
        </GhostStat>
        <GhostStat>
          <Text variant="text-small" color="var(--text-secondary)">
            Duration
          </Text>
          <Text variant="text-regular-md" color="var(--text-secondary)">
            –
          </Text>
        </GhostStat>
        <GhostStat>
          <Text variant="text-small" color="var(--text-secondary)">
            Steps
          </Text>
          <Text variant="text-regular-md" color="var(--text-secondary)">
            –
          </Text>
        </GhostStat>
      </GhostHeader>
      <GhostChart>
        <GhostBarsStack>
          {GHOST_BARS.map((bar, i) => {
            const hasQueue = bar.queue > 0;
            return (
              <GhostBarRow
                // oxlint-disable-next-line react/no-array-index-key -- decorative ghost bars
                key={i}
              >
                {hasQueue && (
                  <GhostBarQueue
                    style={{ left: `${bar.left}%`, width: `${bar.queue}%` }}
                  />
                )}
                <GhostBarRun
                  data-no-queue={hasQueue ? undefined : "true"}
                  style={{
                    left: `${bar.left + bar.queue}%`,
                    width: `${bar.width - bar.queue}%`,
                  }}
                />
              </GhostBarRow>
            );
          })}
        </GhostBarsStack>
      </GhostChart>
      <GhostAxis />
    </GhostWrapper>
  );
}

export function WorkflowRunsPanel({
  workflowId,
  branchId,
  steps,
  hoveredCanonicalId,
  onStepHover,
  onActiveRunStepsChange,
}: Props) {
  const [detailedRuns] = useQuery(
    queries.loadBranchRunsSummary({
      branchId,
    })
  );
  const { byRun: runStatuses } = useBranchRunStatuses(workflowId, branchId);

  const { sortedRuns, totalRunCount } = useMemo(() => {
    const nonEphemeral = detailedRuns.filter((r) => !r.ephemeral);
    const sorted = [...nonEphemeral].sort((a, b) => b.createdAt - a.createdAt);
    return {
      sortedRuns: sorted,
      totalRunCount: nonEphemeral.length,
    };
  }, [detailedRuns]);

  const latestRunId = sortedRuns[0]?.id ?? null;
  const { run: selectedRunId } = useCurrentUrl(routes.space.workflow);
  const activeRunId =
    selectedRunId && sortedRuns.some((r) => r.id === selectedRunId)
      ? selectedRunId
      : latestRunId;
  const activeRun = sortedRuns.find((r) => r.id === activeRunId) ?? null;
  const { executions: activeRunExecutions } = useRunExecutions(activeRunId);

  useEffect(() => {
    onActiveRunStepsChange?.(
      activeRun
        ? activeRunExecutions
            .map((e) => e.stepDir)
            .filter((s): s is string => s != null)
        : null
    );
    return () => onActiveRunStepsChange?.(null);
  }, [activeRun, activeRunExecutions, onActiveRunStepsChange]);

  if (!activeRunId) {
    return (
      <Outer>
        <GhostedWaterfall />
      </Outer>
    );
  }

  return (
    <Outer>
      <RunWaterfall
        runId={activeRunId}
        steps={steps}
        totalRunCount={totalRunCount}
        hoveredCanonicalId={hoveredCanonicalId}
        onStepHover={onStepHover}
        runs={sortedRuns}
        runStatuses={runStatuses}
      />
    </Outer>
  );
}
